/**
 * Game registry — single source of truth mapping `g_game.code` to its React
 * implementation. The public ConsumerSurveyWizard reads this when it
 * encounters a game-type component (gameWheel / specialType+wheelComponent /
 * gameScratch / gameXinkit / etc.) and renders the corresponding game.
 *
 * Adding a new game = new file in `games/`, register its component here.
 * The g_game table stores the metadata; this registry stores the renderer.
 */
import type { FC } from 'react';
import { WheelGame } from '../WheelGame';
import { ScratchCard } from './ScratchCard';
import { Xinkit } from './Xinkit';
import { Pirinola } from './Pirinola';
import { SlotMachine } from './SlotMachine';
import { RandomDraw } from './RandomDraw';
import { SoccerBall } from './SoccerBall';
import { Loteria } from './Loteria';
import type { GamePlayProps } from './pickPrize';

export const GAME_COMPONENTS: Record<string, FC<GamePlayProps>> = {
  // g_game.code → component
  wheel: WheelGame as any,
  scratch: ScratchCard,
  xinkit: Xinkit,
  // `plinko` era el código de Xinkit hasta la migración 092, retirado por
  // marca registrada. Se sigue resolviendo: una migración se aplica donde se
  // lanza, y hasta que 092 corra en un entorno sus despliegues lo piden así.
  plinko: Xinkit,
  pirinola: Pirinola,
  slots: SlotMachine,
  'random-draw': RandomDraw,
  soccer: SoccerBall,
  loteria: Loteria,
};

/**
 * Resolve a game component by either:
 *   - g_game.code (`wheel`, `scratch`, …)
 *   - xy_component.specialField (`wheelComponent`, `scratchComponent`, …)
 *   - xy_component.type (`gameWheel`)
 *
 * Returns `null` if no renderer is registered — caller should render a
 * "Coming soon" placeholder.
 */
export function resolveGame(opts: {
  gameCode?: string | null;
  specialField?: string | null;
  componentType?: string | null;
}): FC<GamePlayProps> | null {
  const { gameCode, specialField, componentType } = opts;

  if (gameCode && GAME_COMPONENTS[gameCode]) return GAME_COMPONENTS[gameCode];

  // specialField follows the convention `<code>Component` (wheelComponent).
  if (specialField && specialField.endsWith('Component')) {
    const code = specialField.replace(/Component$/, '');
    if (GAME_COMPONENTS[code]) return GAME_COMPONENTS[code];
  }

  // Legacy: type='gameWheel' maps to 'wheel'.
  if (componentType === 'gameWheel') return GAME_COMPONENTS.wheel;
  if (componentType === 'gameScratch') return GAME_COMPONENTS.scratch;
  if (componentType === 'gameXinkit' || componentType === 'gamePlinko')
    return GAME_COMPONENTS.xinkit;
  if (componentType === 'gamePirinola') return GAME_COMPONENTS.pirinola;
  if (componentType === 'gameSlots') return GAME_COMPONENTS.slots;
  if (componentType === 'gameRandomDraw') return GAME_COMPONENTS['random-draw'];
  if (componentType === 'gameSoccer') return GAME_COMPONENTS.soccer;
  if (componentType === 'gameLoteria') return GAME_COMPONENTS.loteria;

  return null;
}
